import { useFlag } from '@calendly/feature-check-react'
import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import {
  AllSchedulingMethods,
  MEETINGS_PRESET_IDS,
  MEETINGS_PRESET_PARAM,
  Periods,
  UPCOMING_PRESET_PARAM,
  UPCOMING_PRESET_OFF,
} from 'constants/meetings'
import { isFilterSet } from 'features/meetings/utils'
import { useFilters } from 'hooks/meetings/filters'
import { useQueryParams } from 'hooks/use_query_params'

import { setOpenMeetingDate, setScrollAnchorDate } from '../meetings_page_slice'

/** Query param used to persist an explicit "untoggled" state across query changes. */
export { UPCOMING_PRESET_PARAM }

const PRESET_OFF = UPCOMING_PRESET_OFF

const DATE_PRESET_PARAM_IDS = new Set([
  MEETINGS_PRESET_IDS.TODAY,
  MEETINGS_PRESET_IDS.THIS_WEEK,
  MEETINGS_PRESET_IDS.LAST_WEEK,
])

const PERIOD_BY_PRESET_ID = Object.freeze({
  [MEETINGS_PRESET_IDS.UPCOMING]: Periods.UPCOMING,
  [MEETINGS_PRESET_IDS.TODAY]: Periods.TODAY,
  [MEETINGS_PRESET_IDS.THIS_WEEK]: Periods.THIS_WEEK,
  [MEETINGS_PRESET_IDS.LAST_WEEK]: Periods.LAST_WEEK,
})

const SINGLE_QUERY_PRESET_PERIODS = new Set([
  Periods.TODAY,
  Periods.THIS_WEEK,
  Periods.LAST_WEEK,
])

/**
 * Preset chips only represent a clean default view. The filter bar keeps `period=upcoming`;
 * date presets are stored in `meetings_preset` and applied only to list/export queries.
 */
const isCleanPresetView = (params) => {
  if (params.period !== Periods.UPCOMING) return false
  if (isFilterSet(params)) return false

  const schedulingMethods = params.scheduling_methods || []
  const isSchedulingDefault = schedulingMethods.length === 0
    || schedulingMethods.length >= AllSchedulingMethods.length

  return (
    isSchedulingDefault
    && !params.query
    && !params.start_date
    && !params.end_date
  )
}

const resolveActivePresetId = (queryParams, explicitlyOff) => {
  if (explicitlyOff) return null

  const presetParam = queryParams[MEETINGS_PRESET_PARAM]
  if (presetParam && DATE_PRESET_PARAM_IDS.has(presetParam)) {
    return presetParam
  }

  return MEETINGS_PRESET_IDS.UPCOMING
}

/**
 * Drives the meetings page preset chips (behind `sa_default_preset_toggle`).
 *
 * Presets are mutually exclusive: selecting one turns the others off. Clicking the active chip
 * again untoggles it and restores the legacy meetings page (past + future split, no forward-only).
 *
 * State is derived from URL filter params plus an explicit `upcoming_preset=0` off marker.
 */
export const useUpcomingPreset = () => {
  const presetFlagOn = useFlag('sa_default_preset_toggle', false)
  const queryParams = useQueryParams()
  const dispatch = useDispatch()
  const { getFilterParams, applyFilterParams } = useFilters()

  const filterParams = getFilterParams()

  const explicitlyOff = queryParams[UPCOMING_PRESET_PARAM] === PRESET_OFF

  const activePreset = useMemo(() => {
    if (!presetFlagOn || !isCleanPresetView(filterParams)) return null
    return resolveActivePresetId(queryParams, explicitlyOff)
  }, [presetFlagOn, filterParams, queryParams, explicitlyOff])

  const presetActive = activePreset === MEETINGS_PRESET_IDS.UPCOMING
  const presetPeriod = activePreset && SINGLE_QUERY_PRESET_PERIODS.has(PERIOD_BY_PRESET_ID[activePreset])
    ? PERIOD_BY_PRESET_ID[activePreset]
    : null

  const turnOffPreset = useCallback(() => {
    if (!presetFlagOn) return
    dispatch(setOpenMeetingDate(null))
    dispatch(setScrollAnchorDate(null))
    dispatch(applyFilterParams({
      period: Periods.UPCOMING,
      [UPCOMING_PRESET_PARAM]: PRESET_OFF,
    }))
  }, [presetFlagOn, dispatch, applyFilterParams])

  const selectPreset = useCallback((presetId) => {
    if (!presetFlagOn) return

    dispatch(setOpenMeetingDate(null))
    dispatch(setScrollAnchorDate(null))

    if (presetId === MEETINGS_PRESET_IDS.UPCOMING) {
      dispatch(applyFilterParams({ period: Periods.UPCOMING }))
      return
    }

    if (!DATE_PRESET_PARAM_IDS.has(presetId)) return

    dispatch(applyFilterParams({
      period: Periods.UPCOMING,
      [MEETINGS_PRESET_PARAM]: presetId,
    }))
  }, [presetFlagOn, dispatch, applyFilterParams])

  const togglePreset = useCallback((presetId = MEETINGS_PRESET_IDS.UPCOMING) => {
    if (!presetFlagOn) return
    if (activePreset === presetId) {
      turnOffPreset()
    } else {
      selectPreset(presetId)
    }
  }, [presetFlagOn, activePreset, turnOffPreset, selectPreset])

  return {
    activePreset,
    presetActive,
    presetFlagOn,
    presetPeriod,
    selectPreset,
    togglePreset,
    turnOffPreset,
  }
}
